/**
 * Shared biometric authentication utility
 * Handles WebAuthn authentication and returns standardized results
 */

export interface BiometricAuthResult {
  success: boolean;
  signature?: string;
  credentialId?: string; // The credential ID that was stored in Venly (same as credential.id)
  error?: string;
}

export const authenticateWithBiometric = async (): Promise<BiometricAuthResult> => {
  try {
    // Let WebAuthn choose which credential to use (platform authenticator)
    // It will return the credential that matches the user's biometric
    const credential = await navigator.credentials.get({
      publicKey: {
        challenge: new Uint8Array(32),
        rpId: window.location.hostname,
        userVerification: 'required'
      }
    });
    
    if (credential) {
      // Extract the actual biometric authentication data
      if (credential && 'response' in credential) {
        // Cast to PublicKeyCredential for WebAuthn
        const publicKeyCredential = credential as PublicKeyCredential;
        if (publicKeyCredential.response) {
          // Cast to AuthenticatorAssertionResponse for WebAuthn
          const assertionResponse = publicKeyCredential.response as AuthenticatorAssertionResponse;
          
          // Extract the credential ID - this is what we stored in Venly and must send back
          // credential.id is a base64url-encoded string
          const credentialId = publicKeyCredential.id;
          
          // Extract signature for logging/debugging (not used for Venly authentication)
          const signature = new Uint8Array(assertionResponse.signature);
          const signatureBase64 = btoa(String.fromCharCode.apply(null, Array.from(signature)));
          
          return {
            success: true,
            signature: signatureBase64, // Keep for logging/debugging
            credentialId: credentialId // This is what Venly expects - the same value stored during creation
          };
        }
      }
      // Fallback for other credential types
      return {
        success: false,
        error: 'Unsupported credential type'
      };
    }
    
    return {
      success: false,
      error: 'Biometric authentication failed - no credential returned'
    };
  } catch (error) {
    console.error('Biometric authentication error:', error);
    return {
      success: false,
      error: 'Biometric authentication failed'
    };
  }
};
